// ModernPricingPage.js
import React from "react";
import { RippleButton } from "./RippleButton";
import { toPersianDigits } from "../../../helper/toPersianDigits";

// --- Internal Helper Components (Not exported) --- //

const CheckIcon = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="3"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

// --- EXPORTED Building Blocks --- //

export const PricingCard = ({
  planName,
  description,
  price,
  discountPrice,
  discountPercent,
  features,
  buttonText,
  isPopular = false,
  buttonVariant = "primary",
  onSelect,
  isSelected = false,
}) => {
  const hasDiscount = Boolean(discountPrice);
  const discountLabel = discountPercent
    ? `${toPersianDigits(discountPercent)}٪ تخفیف`
    : "تخفیف";
  const cardClasses = `
    min-w-32 mx-auto rounded-2xl w-full p-1 flex flex-col transition-all duration-300 transform ${isSelected ? "scale-105 translate-y-1" : "scale-100 translate-y-0"}
    bg-zi-bg2-light dark:bg-zi-bg2-dark border border-transparent snap-start
    ${isPopular || hasDiscount ? "relative" : ""}
  `;

  const buttonClasses = `
    w-full py-1 rounded-xl font-normal text-sm transition bg-zi-gradient-button-blue
    ${
      isPopular
      // ? "bg-cyan-400 hover:bg-cyan-300 !text-black"
      // : "border !bg-white/10 hover:bg-white/20 text-white border-white/20"
    }
  `;

  return (
    <div className={cardClasses.trim()}>
      {hasDiscount && (
        <div className="absolute -top-4 right-1/2 translate-x-1/2 whitespace-nowrap px-3 py-1 text-xs font-normal rounded-full bg-zi-blue-light text-zi-text-dark">
          {discountLabel}
        </div>
      )}
      {isSelected && (
        <div className="absolute aspect-square flex items-center justify-center size-6 top-0 right-0 -translate-y-1/2 translate-x-1/2 whitespace-nowrap text-xs font-normal rounded-full bg-zi-blue-light text-zi-text-dark z-10">
          <svg
            className="size-3"
            viewBox="0 0 9 7"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M1 4L3 6L8 1"
              stroke="white"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      )}
      {planName === "Pro" && (
        <div className="absolute aspect-square flex items-center justify-center size-6 top-0 right-0 -translate-y-1/2 translate-x-1/2 whitespace-nowrap text-xs font-normal rounded-full bg-zi-blue-light text-zi-text-dark">
          <svg className="size-3 fill-zi-text-dark" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M9.15316 5.40838C10.4198 3.13613 11.0531 2 12 2C12.9469 2 13.5802 3.13612 14.8468 5.40837L15.1745 5.99623C15.5345 6.64193 15.7144 6.96479 15.9951 7.17781C16.2757 7.39083 16.6251 7.4699 17.3241 7.62805L17.9605 7.77203C20.4201 8.32856 21.65 8.60682 21.9426 9.54773C22.2352 10.4886 21.3968 11.4691 19.7199 13.4299L19.2861 13.9372C18.8096 14.4944 18.5713 14.773 18.4641 15.1177C18.357 15.4624 18.393 15.8341 18.465 16.5776L18.5306 17.2544C18.7841 19.8706 18.9109 21.1787 18.1449 21.7602C17.3788 22.3417 16.2273 21.8115 13.9243 20.7512L13.3285 20.4768C12.6741 20.1755 12.3469 20.0248 12 20.0248C11.6531 20.0248 11.3259 20.1755 10.6715 20.4768L10.0757 20.7512C7.77268 21.8115 6.62118 22.3417 5.85515 21.7602C5.08912 21.1787 5.21588 19.8706 5.4694 17.2544L5.53498 16.5776C5.60703 15.8341 5.64305 15.4624 5.53586 15.1177C5.42868 14.773 5.19043 14.4944 4.71392 13.9372L4.2801 13.4299C2.60325 11.4691 1.76482 10.4886 2.05742 9.54773C2.35002 8.60682 3.57986 8.32856 6.03954 7.77203L6.67589 7.62805C7.37485 7.4699 7.72433 7.39083 8.00494 7.17781C8.28555 6.96479 8.46553 6.64194 8.82547 5.99623L9.15316 5.40838Z"/>
          </svg>
        </div>
      )}

      <div className="mt-1">
        <h2 className="text-2xl text-center font-extralight text-zi-text-light dark:text-zi-text-dark ">
          {planName}
        </h2>
        <div className="w-full flex justify-center items-center">
          <div className="flex flex-col gap-0">
            <p className="font-normal text-zi-text-light dark:text-zi-text-dark mt-0 font-sans">
              {description}
            </p>
            <span className="text-xs text-zi-text-light/70 dark:text-zi-text-dark/70">
              سرمایه اولیه
            </span>
          </div>
        </div>
      </div>

      <div className="w-full flex justify-center items-center my-2">
        <div className="relative flex items-baseline gap-2">
          <span className="text-[14px] text-zi-text-light/70 dark:text-zi-text-dark/70 absolute bottom-0 -right-2 translate-x-full -translate-y-full"></span>
          {!hasDiscount ? (
            <span className="text-2xl font-extralight text-zi-text-light dark:text-zi-text-dark">
              ${price}
            </span>
          ) : (
            <span className="flex items-center gap-4">
              {/* New discounted price */}
              <span className="text-2xl font-extralight text-zi-text-light dark:text-zi-text-dark">
                ${discountPrice}
              </span>
              {/* Old price (striked) */}
              {/* <span className="relative text-xl font-extralight text-zi-text-light/50 dark:text-zi-text-dark/50">
                ${price}
                <span className="absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 w-[120%] h-0.5 bg-zi-border-dark/70 dark:bg-zi-border-dark/95 -rotate-12 rounded-full" />
              </span> */}
            </span>
          )}
        </div>
      </div>

      <div className="hidden card-divider w-full mb-3 sm:mb-5 h-px bg-[linear-gradient(90deg,transparent,rgba(255,255,255,0.09)_20%,rgba(255,255,255,0.22)_50%,rgba(255,255,255,0.09)_80%,transparent)]" />

      <RippleButton className={buttonClasses.trim()} onClick={onSelect}>
        {buttonText}
      </RippleButton>
    </div>
  );
};

export const ChallengeSelect = ({ plans, onPlanSelect, selectedPlan }) => {
  return (
    <main className="relative w-full max-w-5xl min-h-fit flex flex-col items-center justify-center mx-auto">
      <div className="w-full">
        <div className="w-full mx-auto">
          <div className="flex justify-start gap-5 overflow-scroll p-5 snap-x snap-proximity scroll-smooth scroll-pr-3">
            {plans.map((plan, i) => (
              <PricingCard
                {...plan}
                key={plan.planName + "-" + i}
                onSelect={() => onPlanSelect?.(plan.planName)}
                isSelected={selectedPlan === plan.planName}
              />
            ))}
          </div>
        </div>
      </div>
    </main>
  );
};
